import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./Auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProviders = () => {
  const { user } = useAuth();
  const [pendingProviders, setPendingProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") {
      fetchPendingProviders();
    }
  }, [user]);

  const fetchPendingProviders = async () => {
    try {
      const response = await axios.get(`${API}/admin/providers/pending`);
      setPendingProviders(response.data);
    } catch (error) {
      console.error("Failed to fetch pending providers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (providerId, status, notes) => {
    try {
      await axios.post(`${API}/admin/providers/${providerId}/approve`, {
        status,
        verification_notes: notes,
      });
      fetchPendingProviders();
      setSelectedProvider(null);
    } catch (error) {
      alert("Failed to update provider status");
    }
  };

  const handleBackgroundCheck = async (providerId, checkStatus) => {
    try {
      await axios.post(`${API}/admin/providers/${providerId}/background-check`, {
        background_check_status: checkStatus,
        verification_notes: "Updated by admin",
      });
      fetchPendingProviders();
      alert("Background check updated");
    } catch (error) {
      alert("Failed to update background check");
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="requests-screen">
        <div className="empty-state">
          <div className="empty-state-icon">🔒</div>
          <div className="empty-state-text">Admin access required</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="requests-screen" data-testid="admin-providers">
      <div className="requests-header">
        <h1 className="requests-title">Provider Approvals</h1>
        <p className="requests-subtitle">Review and approve new service providers</p>
      </div>

      {pendingProviders.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">✅</div>
          <div className="empty-state-text">No pending providers</div>
        </div>
      ) : (
        <div>
          {pendingProviders.map((provider) => (
            <div key={provider.id} className="request-card" data-testid={`provider-${provider.id}`}>
              <div className="request-header">
                <div>
                  <div className="request-service">{provider.full_name}</div>
                  <div className="request-customer">{provider.email}</div>
                </div>
                <span className="status-badge status-pending">PENDING</span>
              </div>

              <div className="request-details">
                <p>📱 {provider.phone}</p>
                <p>🔨 {provider.service_types?.join(", ")}</p>
                {provider.provider_bio && (
                  <p style={{ marginTop: '8px', fontStyle: 'italic' }}>"{provider.provider_bio}"</p>
                )}
                <p style={{ marginTop: '8px' }}>
                  Background: <span className={`status-badge status-${provider.background_check_status || 'requested'}`}>
                    {provider.background_check_status || "Not Started"}
                  </span>
                </p>
              </div>

              {provider.provider_photo && (
                <div style={{ marginTop: '12px' }}>
                  <img 
                    src={provider.provider_photo} 
                    alt={provider.full_name} 
                    style={{ width: "60px", height: "60px", borderRadius: "50%", objectFit: 'cover' }} 
                  />
                </div>
              )}

              <div className="request-actions">
                <button
                  className="action-btn action-btn-primary"
                  onClick={() => handleApprove(provider.id, "approved", "Approved")}
                >
                  ✓ Approve
                </button>
                <button
                  className="action-btn action-btn-danger"
                  onClick={() => {
                    const reason = prompt("Reason for rejection:");
                    if (reason) handleApprove(provider.id, "rejected", reason);
                  }}
                >
                  ✗ Reject
                </button>
              </div>

              <div style={{ marginTop: '12px' }}>
                <select
                  className="action-btn action-btn-secondary"
                  style={{ width: '100%' }}
                  onChange={(e) => {
                    if (e.target.value) handleBackgroundCheck(provider.id, e.target.value);
                  }}
                  defaultValue=""
                >
                  <option value="">Update Background Check...</option>
                  <option value="in_progress">In Progress</option>
                  <option value="passed">Passed ✓</option>
                  <option value="failed">Failed ✗</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminProviders;
